import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Col,
  Empty,
  Flex,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  CoverageMetric,
  FeedPerformanceTrendPoint,
  useGetFeedPerformanceQuery,
} from "api";
import { format, getRouteByName } from "libs";
import { useCustomToken } from "hooks";
import { useAppSelector } from "store/hooks";

const { Title, Text } = Typography;

/**
 * Faza 8 — Feed performance dashboard (super-admin only).
 *
 * Shows REAL media-pipeline health from the backend: poster/blurhash/faststart/
 * HLS coverage, the processing backlog, the delivery/cache feature flags and
 * the 30-day video upload trend. Metrics that need instrumentation not yet
 * deployed (nginx hit-rate, p95 latency, mobile dropped-frames) are listed as
 * "pending" rather than shown with fabricated numbers.
 */
export const FeedPerformancePage = () => {
  const navigate = useNavigate();
  const { token } = useCustomToken();

  // Super-admin only: only accounts with the `all` permission get `isAll`.
  const { isAll } = useAppSelector(
    (state) => state.loggedAdmin.parsedPermissions
  );

  useEffect(() => {
    if (!isAll) {
      navigate(getRouteByName("NotFoundPage"));
    }
  }, [isAll, navigate]);

  const { data, isLoading, isFetching, refetch, isError } =
    useGetFeedPerformanceQuery();

  const payload = data?.data;

  const coverageColor = (percent: number): string => {
    if (percent >= 90) return token.colorSuccess;
    if (percent >= 60) return token.colorWarning;
    return token.colorError;
  };

  const renderCoverage = (label: string, metric: CoverageMetric) => (
    <Col xs={12} md={8} lg={8} xl={4} key={label}>
      <Flex vertical align="center" gap={8}>
        <Progress
          type="dashboard"
          percent={metric.percent}
          size={120}
          strokeColor={coverageColor(metric.percent)}
        />
        <Text strong>{label}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {metric.done} / {metric.total}
        </Text>
      </Flex>
    </Col>
  );

  const flagTag = (on: boolean, label: string, extra?: string) => (
    <Tag
      icon={on ? <CheckCircleFilled /> : <CloseCircleFilled />}
      color={on ? "success" : "default"}
      style={{ padding: "4px 10px", fontSize: 13, marginBottom: 8 }}
    >
      {label}
      {on && extra ? ` · ${extra}` : ""}
    </Tag>
  );

  const renderTrend = (series: FeedPerformanceTrendPoint[]) => {
    const max = series.reduce((m, p) => Math.max(m, p.videos), 0);
    const totalUploads = series.reduce((s, p) => s + p.videos, 0);

    if (totalUploads === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Niciun clip încărcat în ultimele 30 de zile"
        />
      );
    }

    return (
      <>
        <Flex align="flex-end" gap={3} style={{ height: 140 }}>
          {series.map((p) => {
            const heightPct = max > 0 ? (p.videos / max) * 100 : 0;
            return (
              <Tooltip
                key={p.day}
                title={`${format(p.day, "DD MMM")}: ${p.videos} clip(uri)`}
              >
                <div
                  style={{
                    flex: 1,
                    height: "100%",
                    display: "flex",
                    alignItems: "flex-end",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: `${Math.max(heightPct, p.videos > 0 ? 4 : 0)}%`,
                      minHeight: p.videos > 0 ? 2 : 0,
                      background: token.primaryColor,
                      borderRadius: 3,
                      transition: "height .2s",
                    }}
                  />
                </div>
              </Tooltip>
            );
          })}
        </Flex>
        <Flex justify="space-between" style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {format(series[0]?.day, "DD MMM")}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {totalUploads} clipuri în 30 zile
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {format(series[series.length - 1]?.day, "DD MMM")}
          </Text>
        </Flex>
      </>
    );
  };

  const formatAge = (seconds: number | null): string => {
    if (seconds === null) return "—";
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}z`;
  };

  if (!isAll) return null;

  return (
    <div style={{ padding: 24 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 4 }}>
        <Title level={3} style={{ margin: 0 }}>
          Performanță Feed
        </Title>
        <Tooltip title="Reîmprospătează">
          <ReloadOutlined
            spin={isFetching}
            onClick={() => refetch()}
            style={{ fontSize: 18, cursor: "pointer", color: token.primaryColor }}
          />
        </Tooltip>
      </Flex>
      <Text type="secondary">
        Starea reală a pipeline-ului de media (poster, blurhash, faststart, HLS)
        și a livrării prin cache. Date din baza de date, nu estimări.
      </Text>

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16 }}
          message="Nu am putut încărca datele de performanță."
        />
      )}

      <Spin spinning={isLoading}>
        {payload && (
          <Space direction="vertical" size={16} style={{ width: "100%", marginTop: 16 }}>
            {/* --- Media coverage --- */}
            <Card
              title="Acoperire procesare media"
              extra={
                <Text type="secondary">
                  {payload.media_coverage.videos.total} clipuri ·{" "}
                  {payload.media_coverage.images.total} imagini
                </Text>
              }
            >
              <Row gutter={[16, 24]} justify="center">
                {renderCoverage("Poster", payload.media_coverage.videos.poster)}
                {renderCoverage(
                  "Blurhash video",
                  payload.media_coverage.videos.blurhash
                )}
                {renderCoverage(
                  "Faststart",
                  payload.media_coverage.videos.faststart
                )}
                {renderCoverage("HLS", payload.media_coverage.videos.hls)}
                {renderCoverage(
                  "Blurhash imagini",
                  payload.media_coverage.images.blurhash
                )}
              </Row>
            </Card>

            {/* --- Processing backlog --- */}
            <Card title="Coadă de procesare">
              <Row gutter={[16, 16]}>
                <Col xs={12} md={6}>
                  <Statistic
                    title="HLS în așteptare"
                    value={payload.processing_backlog.pending_transcode}
                    valueStyle={{
                      color:
                        payload.processing_backlog.pending_transcode > 0
                          ? token.colorWarning
                          : token.colorSuccess,
                    }}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic
                    title="Media în așteptare"
                    value={payload.processing_backlog.pending_media}
                    valueStyle={{
                      color:
                        payload.processing_backlog.pending_media > 0
                          ? token.colorWarning
                          : token.colorSuccess,
                    }}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic
                    title="Joburi media eșuate"
                    value={payload.processing_backlog.failed_media}
                    valueStyle={{
                      color:
                        payload.processing_backlog.failed_media > 0
                          ? token.colorError
                          : token.colorSuccess,
                    }}
                    suffix={`/ ${payload.processing_backlog.failed_total} total`}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic
                    title="Cel mai vechi în coadă"
                    value={formatAge(
                      payload.processing_backlog.oldest_pending_seconds
                    )}
                  />
                </Col>
              </Row>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Driver coadă: {payload.processing_backlog.queue_connection}
              </Text>
              {payload.processing_backlog.oldest_pending_seconds !== null &&
                payload.processing_backlog.oldest_pending_seconds > 900 && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 12 }}
                    message="Coada media are joburi mai vechi de 15 minute — verifică dacă worker-ul (inclusiv queue transcode) rulează."
                  />
                )}
            </Card>

            {/* --- Config flags --- */}
            <Card title="Livrare & cache (feature flags)">
              <Space wrap>
                {flagTag(
                  payload.config_flags.public_cache_host,
                  "Cache host public"
                )}
                {flagTag(payload.config_flags.imgproxy, "imgproxy (WebP)")}
                {flagTag(payload.config_flags.hls_delivery, "Livrare HLS")}
                {flagTag(
                  payload.config_flags.feed_cache,
                  "Cache feed",
                  `${payload.config_flags.feed_cache_ttl_seconds}s`
                )}
              </Space>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  URL-uri semnate expiră în{" "}
                  {payload.config_flags.signed_ttl_minutes} min. HLS se activează
                  automat când „Cache host public" e pornit.
                </Text>
              </div>
            </Card>

            {/* --- Upload trend --- */}
            <Card title="Clipuri încărcate (ultimele 30 zile)">
              {renderTrend(payload.upload_trend)}
            </Card>

            {/* --- Pending instrumentation --- */}
            <Card title="Necesită instrumentare (nedisponibil încă)">
              <Alert
                type="info"
                showIcon
                message="Următoarele metrici nu sunt colectate încă"
                description={
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    <li>
                      Rată cache-hit nginx — necesită parsarea log-urilor de acces
                      după deploy-ul cache-ului de media.
                    </li>
                    <li>
                      Latență p50/p95 pe <code>/feed</code> — necesită
                      instrumentare pe backend (middleware de timing).
                    </li>
                    <li>
                      Frame-uri pierdute pe mobil — necesită telemetrie din
                      aplicație în producție.
                    </li>
                  </ul>
                }
              />
            </Card>

            <Text type="secondary" style={{ fontSize: 12 }}>
              Generat: {format(payload.generated_at, "DD MMM YYYY, HH:mm")}
            </Text>
          </Space>
        )}
      </Spin>
    </div>
  );
};
